// Streaming CSV summary used by the audit page's post-export
// confirmation toast.
//
// The CSV download itself is a browser-native streaming save (anchor
// → server `Content-Disposition: attachment`), so the saved file's
// bytes never sit in JS memory. The toast, however, needs to know
// (a) how many real audit rows came back and (b) whether the server
// appended its synthetic "truncated" marker row at the tail. To get
// those signals without buffering the body — which can run to
// hundreds of MB at the 100k-row cap — the page issues a paired
// `fetch` against the same CSV URL and feeds the response body
// chunks through this state machine. Each chunk is consumed and
// discarded as it arrives, so the browser never holds more than a
// single chunk's worth of CSV at any time.
//
// The state machine implements a minimal RFC-4180 reader sufficient
// for the audit export's framing: header row + zero-or-more data
// rows + optional truncation marker row, CRLF row terminator, fields
// double-quoted only when they contain `,`/`"`/`\r`/`\n`, embedded
// quotes escaped as `""`. The parser tracks quote state across
// chunk boundaries (so a field containing a CRLF that straddles two
// chunks doesn't get miscounted as a row break) and remembers the
// start-of-line offset of the current row so it can inspect the
// final row's prefix to detect the marker.
//
// The marker row is recognised by its leading `,,,,truncated,`
// prefix: the export emits the marker with empty createdAt /
// toolName / chatbotId / userId fields and `truncated` in the
// status column. Real audit rows always carry non-empty
// chatbotId / userId / status values (they're DB FK / NOT NULL
// columns), so collisions with a real row are not possible.

const TRUNCATION_PREFIX = ",,,,truncated,";

export interface AuditCsvSummaryState {
  inQuotes: boolean;
  // True iff we hit a lone `"` while inQuotes at the very end of a
  // chunk and still need the next chunk's first character to
  // disambiguate (RFC-4180 escape vs. closing quote). Carried
  // across chunks so a quoted field whose terminating `"` is the
  // last byte of a chunk doesn't get desynced.
  pendingQuoteInQuotes: boolean;
  // Carry-over from the previous chunk: bytes that haven't yet been
  // closed by a CRLF. Specifically the prefix of the current row so
  // we can inspect it for the truncation marker once the row closes.
  pendingRow: string;
  totalRows: number;
  truncated: boolean;
  sawHeader: boolean;
  sawAnyByte: boolean;
}

export function createAuditCsvSummaryState(): AuditCsvSummaryState {
  return {
    inQuotes: false,
    pendingQuoteInQuotes: false,
    pendingRow: "",
    totalRows: 0,
    truncated: false,
    sawHeader: false,
    sawAnyByte: false,
  };
}

// Feed one decoded text chunk into the running state. Chunks must
// arrive in order; the state retains carry-over across calls so the
// caller can pass `value` straight through from a TextDecoderStream
// (or equivalent) without splitting on row boundaries.
export function feedAuditCsvSummary(
  state: AuditCsvSummaryState,
  chunk: string,
): void {
  if (!chunk) return;
  state.sawAnyByte = true;
  let i = 0;
  // Disambiguate a lone `"` carried over from the previous chunk:
  // if the new chunk's first char is also `"` it was an RFC-4180
  // escape (stay inside quotes), otherwise the previous `"` was the
  // field's closing quote (drop out of quote-state and reprocess
  // this character normally).
  if (state.pendingQuoteInQuotes) {
    state.pendingQuoteInQuotes = false;
    const c = chunk[0];
    if (c === '"') {
      state.pendingRow += '"';
      i = 1;
    } else {
      state.inQuotes = false;
      // Fall through with i=0 so this char goes through the normal
      // outside-of-quotes branch below.
    }
  }
  for (; i < chunk.length; i++) {
    const c = chunk[i];
    if (state.inQuotes) {
      if (c === '"') {
        // RFC-4180 escape: a doubled quote inside a quoted field is
        // a literal `"` and does NOT close the field. Peek ahead to
        // disambiguate, looking across the chunk seam if necessary.
        const next = i + 1 < chunk.length ? chunk[i + 1] : undefined;
        if (next === '"') {
          state.pendingRow += '""';
          i++;
          continue;
        }
        if (next === undefined) {
          // Defer the decision to the next chunk: we'll leave
          // `inQuotes` true and set `pendingQuoteInQuotes` so the
          // next call's prologue can resolve escape-vs-close based
          // on what the next byte actually is.
          state.pendingRow += '"';
          state.pendingQuoteInQuotes = true;
          continue;
        }
        state.inQuotes = false;
        state.pendingRow += '"';
        continue;
      }
      state.pendingRow += c;
      continue;
    }
    if (c === '"') {
      state.inQuotes = true;
      state.pendingRow += '"';
      continue;
    }
    if (c === "\r") {
      // Drop bare CRs; the audit export pairs every \r with a \n.
      // (We still tolerate stray CRs by simply discarding them so a
      // mid-field LF terminator doesn't get double-counted.)
      continue;
    }
    if (c === "\n") {
      // End of a row outside any quoted field.
      if (!state.sawHeader) {
        state.sawHeader = true;
      } else {
        // Marker detection — only the LAST data row of the export
        // can carry the truncation marker, but checking the prefix
        // on every row is cheap and means we don't need to know
        // ahead of time which row is the last one.
        if (state.pendingRow.startsWith(TRUNCATION_PREFIX)) {
          state.truncated = true;
        } else {
          state.totalRows++;
        }
      }
      state.pendingRow = "";
      continue;
    }
    state.pendingRow += c;
  }
}

// Finalise the running state once the stream has been fully drained.
// Returns the post-export summary the toast renders.
//
// `dataRows` excludes the header AND the truncation marker (when
// present) — it's the count of real audit rows the reviewer can
// expect to see in the file.
//
// Callers that only have a single string (e.g. unit tests) can use
// `summarizeAuditCsv` below instead of dealing with the streaming
// state directly.
export function finalizeAuditCsvSummary(
  state: AuditCsvSummaryState,
): { dataRows: number; truncated: boolean } {
  // A trailing chunk that didn't end in a CRLF (the audit export
  // always terminates rows with CRLF, but be defensive against an
  // upstream that omits the final terminator).
  if (state.pendingRow.length > 0) {
    if (state.sawHeader) {
      if (state.pendingRow.startsWith(TRUNCATION_PREFIX)) {
        state.truncated = true;
      } else {
        state.totalRows++;
      }
    }
    state.pendingRow = "";
  }
  return { dataRows: state.totalRows, truncated: state.truncated };
}

// Convenience wrapper for callers that already have the full CSV as
// a single string (notably unit tests that build a fixture with the
// real server-side renderer). The streaming path consumes the same
// state machine one chunk at a time, so this and the streaming
// caller will always agree on the summary.
export function summarizeAuditCsv(text: string): { dataRows: number; truncated: boolean } {
  const state = createAuditCsvSummaryState();
  // Strip a leading UTF-8 BOM if present — the audit export emits
  // one for Excel compatibility but it isn't part of the CSV
  // structure proper. The streaming caller doesn't strip it
  // because TextDecoderStream consumes the BOM at the bytes level
  // before any text reaches us.
  if (text.charCodeAt(0) === 0xfeff) {
    feedAuditCsvSummary(state, text.slice(1));
  } else {
    feedAuditCsvSummary(state, text);
  }
  return finalizeAuditCsvSummary(state);
}
